import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import type { OnDocumentLoadProgress, OnDocumentLoadSuccess } from 'react-pdf/dist/esm/shared/types.js';
import { useMeasure } from '../../hooks/useMeasure';
import styles from './style.module.css';

const ESTIMATED_PAGE_HEIGHT = 800;
const RENDER_BUFFER = '400px';

export type PdfProps = {
  url: string;
  page?: number;
  loadingContent?: (progress: number) => React.ReactNode;
  pageLoadingContent?: () => React.ReactNode;
  onVisiblePageChange?: (page: number) => void;
};

export const Pdf = ({ url, page, loadingContent, pageLoadingContent, onVisiblePageChange }: PdfProps) => {
  const [progress, setProgress] = useState(0);
  const [numPages, setNumPages] = useState<number>();
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pageHeights = useRef<Record<number, number>>({});
  const [containerRef, bounds] = useMeasure();
  const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());

  const options = useMemo(
    () => ({
      cMapUrl: '/cmaps/',
      cMapPacked: true,
      standardFontDataUrl: '/standard_fonts/',
    }),
    []
  );

  const onDocumentLoadSuccess: OnDocumentLoadSuccess = useCallback((document) => {
    setNumPages(document.numPages);
  }, []);

  const onProgress: OnDocumentLoadProgress = useCallback(({ loaded, total }) => {
    setProgress((loaded / total) * 100);
  }, []);

  useEffect(() => {
    if (page !== undefined && renderedPages.has(page) && !hasScrolledIntoView) {
      pageRefs.current[page - 1]?.scrollIntoView({ behavior: 'instant' });
      setHasScrolledIntoView(true);
    }
  }, [hasScrolledIntoView, renderedPages, page]);

  const handlePageRenderSuccess = useCallback((pageNumber: number) => {
    setRenderedPages((prev) => new Set(prev).add(pageNumber));
  }, []);

  const handlePageHeightChange = useCallback((pageNumber: number, height: number) => {
    pageHeights.current[pageNumber] = height;
  }, []);

  const defaultLoading = loadingContent ? loadingContent(progress) : null;

  return (
    <div ref={containerRef} className={styles.container}>
      <Document
        key={url}
        file={url}
        loading={defaultLoading}
        onLoadSuccess={onDocumentLoadSuccess}
        onProgress={onProgress}
        options={options}
      >
        {Array.from(new Array(numPages), (_el, index) => {
          const pageNumber = index + 1;
          return (
            <PdfPage
              key={`page_${pageNumber}`}
              pageNumber={pageNumber}
              pageIndex={index}
              width={bounds.width}
              estimatedHeight={pageHeights.current[pageNumber] ?? ESTIMATED_PAGE_HEIGHT}
              forceRender={pageNumber === page}
              pageRefs={pageRefs}
              onRenderSuccess={handlePageRenderSuccess}
              onHeightChange={handlePageHeightChange}
              onVisiblePageChange={onVisiblePageChange}
              pageLoadingContent={pageLoadingContent}
            />
          );
        })}
      </Document>
    </div>
  );
};

type PdfPageProps = {
  pageNumber: number;
  pageIndex: number;
  width: number;
  estimatedHeight: number;
  forceRender: boolean;
  pageRefs: { current: (HTMLDivElement | null)[] };
  onRenderSuccess: (pageNumber: number) => void;
  onHeightChange: (pageNumber: number, height: number) => void;
  onVisiblePageChange?: (page: number) => void;
  pageLoadingContent?: () => React.ReactNode;
};

const PdfPage = memo(
  ({
    pageNumber,
    pageIndex,
    width,
    estimatedHeight,
    forceRender,
    pageRefs,
    onRenderSuccess,
    onHeightChange,
    onVisiblePageChange,
    pageLoadingContent,
  }: PdfPageProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [hasRendered, setHasRendered] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const setRef = useCallback(
      (el: HTMLDivElement | null) => {
        wrapperRef.current = el;
        pageRefs.current[pageIndex] = el;
      },
      [pageRefs, pageIndex]
    );

    useEffect(() => {
      const el = wrapperRef.current;
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        { rootMargin: `${RENDER_BUFFER} 0px ${RENDER_BUFFER} 0px` }
      );

      observer.observe(el);
      return () => observer.disconnect();
    }, []);

    useEffect(() => {
      if (!onVisiblePageChange || !hasRendered) return;

      const el = wrapperRef.current;
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            onVisiblePageChange(pageNumber);
          }
        },
        { threshold: 0.5 }
      );

      observer.observe(el);
      return () => observer.disconnect();
    }, [hasRendered, onVisiblePageChange, pageNumber]);

    const handleRenderSuccess = useCallback(() => {
      setHasRendered(true);
      onRenderSuccess(pageNumber);
      if (wrapperRef.current) {
        onHeightChange(pageNumber, wrapperRef.current.clientHeight);
      }
    }, [pageNumber, onRenderSuccess, onHeightChange]);

    if (!isVisible && !hasRendered && !forceRender) {
      return <div ref={setRef} className={styles.page} style={{ height: estimatedHeight }} />;
    }

    return (
      <Page
        loading={pageLoadingContent?.()}
        pageNumber={pageNumber}
        className={styles.page}
        inputRef={setRef}
        onRenderSuccess={handleRenderSuccess}
        width={width}
      />
    );
  }
);
